import { CanvasEx } from '@/typings/canvas';
import { delay } from '@/utils/timingUtils';
import { updateElementsOrderFromZIndex } from '@/utils/elementsUtils';
import { BoundedTextElement, CanvasElement, ImagePlaceholderElement } from '@/typings/customilyElements/canvasElements';
import { ValueFormat } from '@/typings/designStudio/sourceElementTypes';

export const TEXT_MASK_EFFECTS = {
    mask: {
        prop: 'useAsMask' as const,
        valueFormat: ValueFormat.TextAsMask,
        inverted: false
    },
    cutout: {
        prop: 'useAsCutout' as const,
        valueFormat: ValueFormat.TextAsCutout,
        inverted: true
    }
};

export interface ElementsLink {
    sourceId: string;
    sourceType: number;
    targetId: string;
    targetType: number;
    valueFormat: ValueFormat;
}

export interface TextEffectApplyResult {
    applied: boolean;
    targetObject?: ImagePlaceholderElement;
}

export interface TextEffectConfig {
    prop: 'useAsMask' | 'useAsCutout';
    valueFormat: ValueFormat;
    inverted: boolean;
    applyFn: (uuid: string, textObject?: BoundedTextElement) => Promise<TextEffectApplyResult>;
    removeFn: (
        targetObject: ImagePlaceholderElement,
        textObject: BoundedTextElement | null
    ) => Promise<ImagePlaceholderElement | null>;
    addLink: (textUuid: string, imageUuid: string) => void;
    removeLink: (textUuid: string) => void;
}

export interface DesignStudioScope {
    editMode: boolean;
    textAsMaskStoredObjects: Record<string, { fontColor: unknown; strokeColor: unknown; strokeWidth: number }>;
    forDesignTemplateCreation?: boolean;
    isUserDesignCreation?: boolean;
    useAutofill?: (value: boolean) => void;
}

export interface TextServiceLike {
    applyTextMask: (
        canvas: CanvasEx,
        $scope: DesignStudioScope,
        uuid: string,
        text?: BoundedTextElement
    ) => Promise<TextEffectApplyResult>;
    applyTextCutout: (
        canvas: CanvasEx,
        $scope: DesignStudioScope,
        uuid: string,
        text?: BoundedTextElement
    ) => Promise<TextEffectApplyResult>;
    removeTextMask: (
        canvas: CanvasEx,
        $scope: DesignStudioScope,
        targetObject: ImagePlaceholderElement,
        textObject: BoundedTextElement | null
    ) => Promise<ImagePlaceholderElement | null>;
    removeTextCutout: (
        canvas: CanvasEx,
        $scope: DesignStudioScope,
        targetObject: ImagePlaceholderElement,
        textObject: BoundedTextElement | null
    ) => Promise<ImagePlaceholderElement | null>;
    setTextMaskColor: (canvas: CanvasEx, $scope: DesignStudioScope, object: BoundedTextElement) => void;
    setTextMaskStroke: (canvas: CanvasEx, $scope: DesignStudioScope, object: BoundedTextElement) => void;
}

export interface ProductServiceLike {
    addTextMaskElementsLink: (textUuid: string, imageUuid: string) => void;
    removeTextMaskElementsLink: (textUuid: string) => void;
    addTextCutoutElementsLink: (textUuid: string, imageUuid: string) => void;
    removeTextCutoutElementsLink: (textUuid: string) => void;
    getElementsLinks: () => ElementsLink[];
}

export interface DynamicImageServiceLike {
    showDynamicImage: (canvas: CanvasEx, targetObject: ImagePlaceholderElement, variation: number) => Promise<void>;
}

export interface TestImageServiceLike {
    showStaticImage: (canvas: CanvasEx, targetObject: ImagePlaceholderElement, options: { imageUrl: string }) => void;
}

interface CreateTextEffectParams {
    canvas?: CanvasEx;
    getCanvas?: () => CanvasEx;
    $scope: DesignStudioScope;
    TextService: TextServiceLike;
    ProductService: ProductServiceLike;
}

export const createTextEffect = ({
    canvas,
    getCanvas,
    $scope,
    TextService,
    ProductService
}: CreateTextEffectParams): Record<'mask' | 'cutout', TextEffectConfig> => {
    const resolveCanvas = getCanvas || (() => canvas as CanvasEx);
    return {
        mask: {
            ...TEXT_MASK_EFFECTS.mask,
            applyFn: (uuid, textObject) => TextService.applyTextMask(resolveCanvas(), $scope, uuid, textObject),
            removeFn: (targetObject, textObject) =>
                TextService.removeTextMask(resolveCanvas(), $scope, targetObject, textObject),
            addLink: (textUuid, imageUuid) => ProductService.addTextMaskElementsLink(textUuid, imageUuid),
            removeLink: (textUuid) => ProductService.removeTextMaskElementsLink(textUuid)
        },
        cutout: {
            ...TEXT_MASK_EFFECTS.cutout,
            applyFn: (uuid, textObject) => TextService.applyTextCutout(resolveCanvas(), $scope, uuid, textObject),
            removeFn: (targetObject, textObject) =>
                TextService.removeTextCutout(resolveCanvas(), $scope, targetObject, textObject),
            addLink: (textUuid, imageUuid) => ProductService.addTextCutoutElementsLink(textUuid, imageUuid),
            removeLink: (textUuid) => ProductService.removeTextCutoutElementsLink(textUuid)
        }
    };
};

interface CreateTextEffectHandlersParams {
    getCanvas: () => CanvasEx;
    $scope: DesignStudioScope;
    DynamicImageService: DynamicImageServiceLike;
    TestImageService: TestImageServiceLike;
    TextService: TextServiceLike;
    ProductService: ProductServiceLike;
}

export const createTextEffectHandlers = ({
    getCanvas,
    $scope,
    DynamicImageService,
    TestImageService,
    TextService,
    ProductService
}: CreateTextEffectHandlersParams) => {
    const applyTextEffect = async (uuid: string, cfg: TextEffectConfig) => {
        if ($scope.forDesignTemplateCreation || $scope.isUserDesignCreation) return;

        const canvas = getCanvas();
        const result = await cfg.applyFn(uuid);
        updateElementsOrderFromZIndex(canvas);
        if (!result?.applied && result?.targetObject) {
            await removeTextEffect(result.targetObject, null, cfg);
        } else if (result?.applied && result?.targetObject) {
            cfg.addLink(uuid, result.targetObject.uuid as string);
        }
    };

    const removeTextEffect = async (
        targetObject: ImagePlaceholderElement,
        textObject: BoundedTextElement | null,
        cfg: TextEffectConfig
    ) => {
        const canvas = getCanvas();
        const updatedTarget = await cfg.removeFn(targetObject, textObject);
        if (!updatedTarget) return;
        if (updatedTarget.dynamicImagesPath) {
            await DynamicImageService.showDynamicImage(canvas, updatedTarget, updatedTarget.dynamicImageVariation || 1);
        } else {
            TestImageService.showStaticImage(canvas, updatedTarget, {
                imageUrl: updatedTarget.testingUrl || require('@/assets/images/photo-example.png')
            });
        }
    };

    const updateTextEffect = async (
        active: boolean,
        object: BoundedTextElement,
        reorderLayers: boolean,
        cfg: TextEffectConfig
    ) => {
        if ($scope.forDesignTemplateCreation || $scope.isUserDesignCreation) return;

        if (reorderLayers) await delay(1000);
        const canvas = getCanvas();
        if ($scope.editMode) object.setEditedProp?.(cfg.prop);

        object.set(cfg.prop, active);

        if (active) {
            if (!$scope.textAsMaskStoredObjects[object.uuid as string]) {
                $scope.textAsMaskStoredObjects[object.uuid as string] = {
                    fontColor: object.getColor(),
                    strokeColor: object.getOutlineColor(),
                    strokeWidth: object.getOutlineWidth()
                };
                TextService.setTextMaskColor(canvas, $scope, object);
                TextService.setTextMaskStroke(canvas, $scope, object);
            }

            object.text ||= 'A';

            const result = await cfg.applyFn(object.uuid as string, object);
            updateElementsOrderFromZIndex(canvas);
            if (!result?.applied && result?.targetObject) {
                await removeTextEffect(result.targetObject, null, cfg);
            } else if (result?.applied && result?.targetObject) {
                cfg.addLink(object.uuid as string, result.targetObject.uuid as string);
            }

            $scope.useAutofill?.(true);
        } else {
            const existingLink = ProductService.getElementsLinks().find(
                (l) => l.sourceId === object.uuid && l.valueFormat === cfg.valueFormat
            );
            const target = existingLink
                ? (canvas._objects.find((o) => (o as ImagePlaceholderElement).uuid === existingLink.targetId) as
                      | ImagePlaceholderElement
                      | undefined)
                : null;

            cfg.removeLink(object.uuid as string);
            if (target) await removeTextEffect(target, object, cfg);

            updateElementsOrderFromZIndex(canvas);
        }
    };

    return { applyTextEffect, removeTextEffect, updateTextEffect };
};

/**
 * Cleans up mask/cutout ElementsLinks and re-applied effects when elements are deleted from the canvas:
 * - a deleted mask/cutout text drops its own link
 * - a deleted target image drops the link pointing at it
 * - any surviving target image whose linked text was deleted has its effect removed
 */
export const cleanupDeletedTextEffects = async (
    canvas: CanvasEx,
    deleted: CanvasElement[],
    ProductService: ProductServiceLike,
    effects: Record<'mask' | 'cutout', TextEffectConfig>,
    removeTextEffect: (
        targetObject: ImagePlaceholderElement,
        textObject: BoundedTextElement | null,
        cfg: TextEffectConfig
    ) => Promise<void>
) => {
    const deletedUuids = new Set(deleted.map((u) => u.uuid));
    const links = ProductService.getElementsLinks();
    const effectList = [effects.mask, effects.cutout];

    const imagesToCleanByEffect = effectList.map((cfg) => ({
        cfg,
        imagesToClean: ((canvas._objects as unknown) as ImagePlaceholderElement[]).filter(
            (o) =>
                o.type === 'image-placeholder' &&
                links.some(
                    (l) => l.targetId === o.uuid && l.valueFormat === cfg.valueFormat && deletedUuids.has(l.sourceId)
                )
        )
    }));

    for (const u of deleted) {
        if (u.type === 'bounded-text') {
            if (u.useAsMask) effects.mask.removeLink(u.uuid as string);
            if (u.useAsCutout) effects.cutout.removeLink(u.uuid as string);
        }
        if (u.type === 'image-placeholder') {
            for (const cfg of effectList) {
                const link = links.find((l) => l.targetId === u.uuid && l.valueFormat === cfg.valueFormat);
                if (link) cfg.removeLink(link.sourceId);
            }
        }
    }

    for (const { cfg, imagesToClean } of imagesToCleanByEffect) {
        for (const object of imagesToClean) {
            await removeTextEffect(object, null, cfg);
        }
    }
};
